<template>
    <div class="tw:flex tw:flex-col tw:gap-2 tw:w-full" :class="{ 'ebook-panel-upload': isPanelVariant }">
        <div class="tw:flex tw:flex-col tw:items-center tw:w-full">
            <e-upload
                v-model="filesToUpload"
                :disabled="disabled"
                class="is-flex tw:w-full"
                multiple
                :expanded="expanded"
                drag-drop
                :upload-context="uploadContext"
                @input="updateMedia"
            >
                <section v-if="isPanelVariant" class="ebook-panel-upload__panel">
                    <div class="ebook-panel-upload__inner">
                        <span class="ebook-panel-upload__button tw:text-base">
                            <e-icon icon="upload" size="is-small" class="tw:mr-1" />
                            Upload
                        </span>
                        <p class="ebook-panel-upload__hint tw:text-base">
                            {{ panelHint }}
                        </p>
                    </div>
                </section>
                <section v-else class="section content">
                    <div class="has-text-centered">
                        <p class="m-b-md">
                            <e-icon icon="cloud-upload" size="is-large" />
                        </p>

                        <p>
                            {{ translate('drop_your_files_here_or_click_to_upload') }}
                        </p>
                    </div>
                </section>
            </e-upload>

            <upload-constraints-hint
                v-if="showConstraints && !isPanelVariant"
                :extensions="allowedExtensions"
                :max-size-display="maxFileSizeDisplay"
            />
        </div>

        <div v-if="isUploading">
            <e-progress size="is-small" type="is-primary" indeterminate style="height: 0.5rem" />
        </div>

        <div v-else-if="!isEmpty(media) || !isEmpty(localMedia)">
            <attachments
                label=""
                :deletable="deletable"
                :media="media || localMedia"
                :viewer-context="resolvedViewerContext"
                inline
                @delete="deleteFile"
            />
        </div>
    </div>
</template>

<script>
import isEmpty from 'lodash/isEmpty.js';
import { useI18n } from 'vue-i18n';

import Attachments from '@/components/activity-types/course-activity-types/activities/Attachments.vue';
import UploadConstraintsHint from '@/components/elements/UploadConstraintsHint.vue';
import { FILE_UPLOAD_CONTEXT, FILE_UPLOAD_REJECTION_REASON } from '@/constants/fileUploadRejection.js';
import { getBase64, initialState } from '@/Helpers.js';
import CheckFilesMixin from '@/mixins/CheckFiles.mixin.js';
import { reportFileUploadRejection } from '@/utils/reportFileUploadRejection.js';

function storeField(name) {
    return {
        get() {
            return this.getField(name);
        },
        set(value) {
            this.setField(name, value);
        },
    };
}

export default {
    name: 'EdverumFileUpload',

    components: { Attachments, UploadConstraintsHint },

    mixins: [CheckFilesMixin],

    props: {
        namespace: {
            type: String,
            default: null,
        },

        uuid: {
            type: String,
            default: null,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        autosave: {
            type: Boolean,
            default: true,
        },

        attachments: {
            type: [Array, Object],
            default: null,
        },

        deletable: {
            type: Boolean,
            default: true,
        },

        uploadLimit: {
            type: Number,
            default: 50,
        },

        reset: {
            type: Boolean,
            default: false,
        },

        expanded: {
            type: Boolean,
            default: false,
        },

        /**
         * Whether to show the upload constraints hint below the upload area
         */
        showConstraints: {
            type: Boolean,
            default: true,
        },

        /**
         * Human-readable max file size string (e.g., '10 MB')
         * Defaults to the global MaxFileSizeDisplay from app config
         */
        maxFileSizeDisplay: {
            type: String,
            default: () => initialState('MaxFileSizeDisplay') || '',
        },

        /**
         * Array of allowed file extensions (e.g., ['.pdf', '.doc'])
         * Empty array means all file types are allowed
         */
        allowedExtensions: {
            type: Array,
            default: () => [],
        },

        /**
         * Visual style for the empty upload zone.
         * - default: cloud icon + drop text
         * - panel: dashed box with Upload button (ebook template mockup)
         */
        variant: {
            type: String,
            default: 'default',
            validator: value => ['default', 'panel'].includes(value),
        },

        panelHint: {
            type: String,
            default: 'Upload PDF, Word, PowerPoint, images, audio, or video files',
        },

        /**
         * When false, emitted media items keep the original File/Blob on `file` (multipart uploads).
         * When true (default), files are read as base64 in `data` for JSON/autosave payloads.
         */
        encodeAsBase64: {
            type: Boolean,
            default: true,
        },

        /**
         * Where rejected files are logged. Per-file size rejections are logged by CheckFilesMixin.
         */
        uploadContext: {
            type: String,
            default: FILE_UPLOAD_CONTEXT.UNSPECIFIED,
        },

        /**
         * ADR-052 File Viewer launch context forwarded to Attachments.
         */
        viewerContext: {
            type: Object,
            required: false,
            default: null,
        },
    },

    emits: ['add', 'delete', 'error'],

    data: () => ({
        localMedia: [],
        filesToUpload: [],
    }),

    setup() {
        const { t: translate } = useI18n();
        return { translate };
    },

    computed: {
        media: storeField('media'),

        isUploading() {
            return this.uuid && this.$store?.state?.AutoSave?.uploadingMedia === this.uuid;
        },

        isPanelVariant() {
            return this.variant === 'panel';
        },

        resolvedViewerContext() {
            return {
                useCase: this.viewerContext?.useCase || 'submission',
                courseName: this.viewerContext?.courseName ?? null,
                studentName: this.viewerContext?.studentName ?? null,
                activityName: this.viewerContext?.activityName ?? null,
            };
        },
    },

    watch: {
        filesToUpload: {
            handler(files) {
                if (!files.length) {
                    return;
                }
                // updateMedia already emits `error`; swallow the rethrow so the watcher
                // does not leave an unhandled rejection.
                this.updateMedia(files).catch(() => {});
            },
            deep: true,
        },

        reset: {
            handler(reset) {
                if (reset) {
                    this.filesToUpload = [];
                    this.localMedia = [];
                }
            },
            immediate: true,
        },
    },

    created() {
        if (this.attachments) {
            this.localMedia = this.attachments;
        }
    },

    methods: {
        isEmpty,

        getField(field) {
            if (this.autosave) {
                return this.$store.getters[`${this.namespace}/${this.uuid}/get`](field);
            }

            return null;
        },

        setField(field, value) {
            if (this.autosave) {
                return this.$store.dispatch(`${this.namespace}/updateItem`, {
                    uuid: this.uuid,
                    field,
                    value,
                });
            }

            return null;
        },

        async updateMedia(files) {
            if (!this.filesAreValid(files)) {
                this.filesToUpload = [];
                this.localMedia = [];

                return undefined;
            }

            if (this.uploadLimit && files.length > 0) {
                const MAX_TOTAL_SIZE = this.uploadLimit * 1024 * 1024;

                const totalSize = files.reduce((sum, file) => {
                    return sum + (file.size || 0);
                }, 0);

                if (totalSize > MAX_TOTAL_SIZE) {
                    // The pop below re-triggers the deep watcher, which re-runs this check on the
                    // remaining files, so each pass logs only the file it drops.
                    reportFileUploadRejection(
                        this.uploadContext,
                        files[files.length - 1],
                        FILE_UPLOAD_REJECTION_REASON.TOTAL_SIZE_EXCEEDED,
                        { maxFileSizeBytes: MAX_TOTAL_SIZE }
                    );
                    this.$emit('error', {
                        media: [`Total file size exceeds ${this.uploadLimit} MB.`],
                    });

                    files.pop();
                    return undefined;
                }
            }

            if (!files.length) return undefined;

            const media = [];
            let fileBeingRead = null;

            try {
                // eslint-disable-next-line no-restricted-syntax
                for (const file of files) {
                    fileBeingRead = file;
                    const entry = {
                        name: file.name,
                        type: file.type,
                        size: file.size,
                    };

                    if (this.encodeAsBase64) {
                        // eslint-disable-next-line no-await-in-loop
                        entry.data = await getBase64(file);
                    } else {
                        // preserve the original File for multipart uploads
                        entry.file = file;
                    }

                    media.push(entry);
                }
            } catch (error) {
                reportFileUploadRejection(this.uploadContext, fileBeingRead, FILE_UPLOAD_REJECTION_REASON.INVALID_FILE);
                this.filesToUpload = [];
                this.localMedia = [];
                this.$emit('error', {
                    media: ['Unable to read the selected file.'],
                });
                // Re-throw to allow callers/tests to detect conversion failures
                throw error;
            }

            if (this.autosave) {
                this.$store.commit('AutoSave/UPLOADING_MEDIA', this.uuid);

                await this.setField('media', media);
            }

            if (!this.autosave) {
                // this.localMedia = media;
                //
                // this.$emit('add', media);

                const currentMedias = this.attachments ? this.attachments : [];

                this.localMedia = [...currentMedias, ...media];

                this.$emit('add', this.localMedia);
            }

            this.filesToUpload = [];
            return undefined;
        },

        deleteFile({ item, index }) {
            if (this.autosave) {
                this.$store.commit(`${this.namespace}/DELETE_MEDIA`, {
                    uuid: this.uuid,
                    field: 'media',
                    media: this.media[index],
                });

                return this.media.splice(index, 1);
            }

            this.localMedia.splice(index, 1);

            return this.$emit('delete', index, item);
        },
    },
};
</script>

<style scoped>
.ebook-panel-upload :deep(.upload-draggable) {
    width: 100%;
    min-height: 180px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
}

.ebook-panel-upload__panel {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 0;
}

.ebook-panel-upload__inner {
    text-align: center;
}

.ebook-panel-upload__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    background: var(--p-primary-color, #2563eb);
    color: #fff;
    border-radius: 6px;
    padding: 0.55rem 1.1rem;
    font-weight: 600;
    line-height: 1.2;
}

.ebook-panel-upload__hint {
    margin: 0.85rem 0 0;
    color: #64748b;
}
</style>
